import { useState, useMemo } from 'react'
import { AlertTriangle, Shield, Zap, Search, Activity, Smartphone, Wifi, Lock, Target, Trash2, Settings, Tablet, Laptop, HardDrive, Eye, Users, Sliders, Unlock, Database, BarChart3 } from 'lucide-react'

export default function DeviceProtectionPage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [filterStatus, setFilterStatus] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  const [devices] = useState([
    { id: 'DEV-001', name: 'John Smith iPhone 15', type: 'Mobile', os: 'iOS 17', status: 'Secure', health: 96, threats: 0, policies: 5, lastCheck: '2 mins ago', compliance: 'Full' },
    { id: 'DEV-002', name: 'Sarah Johnson MacBook Pro', type: 'Laptop', os: 'macOS Sonoma', status: 'Secure', health: 94, threats: 0, policies: 5, lastCheck: '5 mins ago', compliance: 'Full' },
    { id: 'DEV-003', name: 'Office Printer HP M428', type: 'IoT', os: 'HP Firmware 5.1', status: 'Warning', health: 78, threats: 1, policies: 3, lastCheck: '12 mins ago', compliance: 'Partial' },
    { id: 'DEV-004', name: 'Mike Davis Samsung Tablet', type: 'Tablet', os: 'Android 14', status: 'At Risk', health: 61, threats: 3, policies: 2, lastCheck: '18 mins ago', compliance: 'Failed' },
    { id: 'DEV-005', name: 'Conference Room Smart TV', type: 'IoT', os: 'WebOS 24', status: 'Secure', health: 89, threats: 0, policies: 3, lastCheck: '3 mins ago', compliance: 'Full' },
    { id: 'DEV-006', name: 'Emily Zhang Windows Laptop', type: 'Laptop', os: 'Windows 11 Pro', status: 'Warning', health: 82, threats: 2, policies: 5, lastCheck: '8 mins ago', compliance: 'Partial' },
  ])

  const [policies] = useState([
    { id: 'POL-001', name: 'Encryption Required', type: 'Security', devices: 14, status: 'Enforced' },
    { id: 'POL-002', name: 'App Whitelist', type: 'Control', devices: 12, status: 'Enforced' },
    { id: 'POL-003', name: 'Password Policy', type: 'Authentication', devices: 18, status: 'Enforced' },
    { id: 'POL-004', name: 'USB Restrictions', type: 'Control', devices: 15, status: 'Enforced' },
    { id: 'POL-005', name: 'Update Enforcement', type: 'Maintenance', devices: 16, status: 'Warning' },
  ])

  const [lostDevices] = useState([
    { id: 'LOST-001', name: 'Alex Wong Samsung Galaxy S23', reportedTime: '2 hours ago', location: 'Coffee Shop Downtown', status: 'Tracking' },
    { id: 'LOST-002', name: 'Rachel Kim iPad Air', reportedTime: '1 day ago', location: 'Unknown', status: 'Locked' },
  ])

  const filteredDevices = useMemo(() => {
    return devices.filter(d => {
      const matchStatus = filterStatus === 'all' || d.status === filterStatus
      const matchSearch = searchQuery === '' ||
        d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.os.toLowerCase().includes(searchQuery.toLowerCase())
      return matchStatus && matchSearch
    })
  }, [devices, filterStatus, searchQuery])

  const deviceStats = [
    { label: 'Total Devices', value: devices.length, icon: Smartphone, color: 'text-blue-400' },
    { label: 'Secure', value: devices.filter(d => d.status === 'Secure').length, icon: Shield, color: 'text-green-400' },
    { label: 'At Risk', value: devices.filter(d => d.status === 'At Risk').length, icon: AlertTriangle, color: 'text-red-400' },
    { label: 'Lost/Stolen', value: lostDevices.length, icon: Zap, color: 'text-yellow-400' },
  ]

  const deviceActions = [
    { id: 1, label: 'Remote Lock', icon: Lock, color: 'blue', desc: 'Lock device remotely' },
    { id: 2, label: 'Wipe Data', icon: Trash2, color: 'red', desc: 'Erase all device data' },
    { id: 3, label: 'Track Device', icon: Target, color: 'purple', desc: 'Real-time GPS tracking' },
    { id: 4, label: 'Update Policies', icon: Sliders, color: 'cyan', desc: 'Push policy updates' },
    { id: 5, label: 'Request Unlock', icon: Unlock, color: 'orange', desc: 'Request user unlock' },
    { id: 6, label: 'View Device Details', icon: Eye, color: 'green', desc: 'Full device inventory' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Premium Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 p-8 text-white shadow-2xl border border-cyan-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-4xl font-black">SENTINEL SHIELD MDM</h1>
                <p className="text-cyan-100 text-lg font-semibold mt-1">Mobile Device Management & Enterprise Protection</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-cyan-300">{devices.length}</div>
                <div className="text-sm text-cyan-100">Devices Managed</div>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 w-5 h-5 text-cyan-200/50" />
                <input
                  type="text"
                  placeholder="Search devices, users, models..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-black/20 backdrop-blur-sm border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white placeholder-cyan-200/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                />
              </div>
              <button className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-bold transition-all border border-white/20">Search</button>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {deviceStats.map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 p-5 hover:border-slate-600 transition-all shadow-lg">
                <div className="flex items-start justify-between mb-3">
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                  <span className="text-xs font-bold text-green-400">LIVE</span>
                </div>
                <p className="text-slate-400 text-sm font-semibold">{stat.label}</p>
                <p className="text-white text-3xl font-black mt-2">{stat.value}</p>
              </div>
            )
          })}
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-slate-700 overflow-x-auto pb-4">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'devices', label: 'Device Inventory', icon: Smartphone },
            { id: 'policies', label: 'Compliance', icon: Sliders },
            { id: 'lost', label: 'Lost & Stolen', icon: Zap },
            { id: 'response', label: 'Response', icon: Zap },
          ].map((tab) => {
            const TabIcon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-semibold whitespace-nowrap transition-all border-b-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-slate-400 hover:text-slate-300'
                }`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 p-6 shadow-lg">
                <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-cyan-400" />
                  Device Type Distribution
                </h3>
                <div className="space-y-3">
                  {[
                    { type: 'Mobile Devices', count: 3, color: 'bg-blue-500' },
                    { type: 'Laptops', count: 2, color: 'bg-purple-500' },
                    { type: 'IoT Devices', count: 2, color: 'bg-cyan-500' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-slate-300 text-sm">{item.type}</span>
                          <span className="text-white font-bold">{item.count}</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div className={`${item.color} h-2 rounded-full`} style={{width: `${(item.count / 9) * 100}%`}}></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 p-6 shadow-lg">
                <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-400" />
                  Compliance Status
                </h3>
                <div className="space-y-3">
                  {[
                    { status: 'Fully Compliant', count: 3, color: 'bg-green-500' },
                    { status: 'Partially Compliant', count: 2, color: 'bg-yellow-500' },
                    { status: 'Non-Compliant', count: 1, color: 'bg-red-500' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className={`${item.color} w-3 h-3 rounded-full`}></div>
                      <span className="text-slate-300 text-sm flex-1">{item.status}</span>
                      <span className="text-white font-bold text-sm">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 p-6 shadow-lg">
              <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-indigo-400" />
                Sentinel Shield Capabilities
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { icon: Lock, label: 'Device Enrollment' },
                  { icon: Sliders, label: 'Policy Management' },
                  { icon: Target, label: 'Asset Tracking' },
                  { icon: Users, label: 'User Management' },
                  { icon: HardDrive, label: 'Data Management' },
                  { icon: Activity, label: 'Threat Protection' },
                  { icon: Database, label: 'Backup & Recovery' },
                  { icon: Eye, label: 'Security Monitoring' },
                ].map((item, idx) => {
                  const Icon = item.icon
                  return (
                    <div key={idx} className="bg-slate-700/50 border border-slate-600 rounded-lg p-3 flex flex-col items-center gap-2 hover:bg-slate-700 transition-all cursor-pointer">
                      <Icon className="w-5 h-5 text-cyan-400" />
                      <span className="text-slate-300 text-xs text-center">{item.label}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* Device Inventory Tab */}
        {activeTab === 'devices' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-bold text-lg">Managed Devices</h3>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="bg-slate-800 border border-slate-600 text-white px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="all">All Status</option>
                <option value="Secure">Secure</option>
                <option value="Warning">Warning</option>
                <option value="At Risk">At Risk</option>
              </select>
            </div>

            {filteredDevices.map((device) => (
              <div key={device.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 p-5 hover:border-slate-600 transition-all shadow-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        {device.type === 'Mobile' && <Smartphone className="w-5 h-5 text-blue-400" />}
                        {device.type === 'Laptop' && <Laptop className="w-5 h-5 text-purple-400" />}
                        {device.type === 'Tablet' && <Tablet className="w-5 h-5 text-green-400" />}
                        {device.type === 'IoT' && <Wifi className="w-5 h-5 text-orange-400" />}
                        <h4 className="text-white font-bold">{device.name}</h4>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        device.status === 'Secure' ? 'bg-green-500/20 text-green-400' :
                        device.status === 'Warning' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {device.status}
                      </span>
                    </div>
                    <div className="text-slate-400 text-sm mb-2">{device.type} • {device.os}</div>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <div className="text-slate-400 text-xs">Health</div>
                        <div className="text-white font-bold">{device.health}%</div>
                      </div>
                      <div>
                        <div className="text-slate-400 text-xs">Threats</div>
                        <div className="text-white font-bold">{device.threats}</div>
                      </div>
                      <div>
                        <div className="text-slate-400 text-xs">Policies</div>
                        <div className="text-white font-bold">{device.policies}</div>
                      </div>
                      <div>
                        <div className="text-slate-400 text-xs">Compliance</div>
                        <div className={`font-bold text-sm ${
                          device.compliance === 'Full' ? 'text-green-400' : 'text-yellow-400'
                        }`}>{device.compliance}</div>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-slate-400 text-xs mb-2">Last Check</div>
                    <div className="text-white text-sm font-semibold">{device.lastCheck}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Compliance Policies Tab */}
        {activeTab === 'policies' && (
          <div className="space-y-4">
            <h3 className="text-white font-bold text-lg">Active Compliance Policies</h3>
            {policies.map((policy) => (
              <div key={policy.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 p-5 hover:border-slate-600 transition-all shadow-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-white font-bold mb-2">{policy.name}</h4>
                    <div className="flex gap-4">
                      <div>
                        <div className="text-slate-400 text-xs">Type</div>
                        <div className="text-slate-300 text-sm">{policy.type}</div>
                      </div>
                      <div>
                        <div className="text-slate-400 text-xs">Devices</div>
                        <div className="text-slate-300 text-sm">{policy.devices}</div>
                      </div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    policy.status === 'Enforced' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {policy.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Lost & Stolen Tab */}
        {activeTab === 'lost' && (
          <div className="space-y-4">
            <h3 className="text-white font-bold text-lg">Lost & Stolen Devices</h3>
            {lostDevices.map((device) => (
              <div key={device.id} className="bg-gradient-to-br from-red-900/20 to-slate-900 rounded-xl border border-red-500/30 p-5 shadow-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-white font-bold mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      {device.name}
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-slate-400 text-xs">Reported</div>
                        <div className="text-slate-300 text-sm">{device.reportedTime}</div>
                      </div>
                      <div>
                        <div className="text-slate-400 text-xs">Last Location</div>
                        <div className="text-slate-300 text-sm">{device.location}</div>
                      </div>
                      <div>
                        <div className="text-slate-400 text-xs">Status</div>
                        <div className={`text-sm font-bold ${device.status === 'Tracking' ? 'text-cyan-400' : 'text-red-400'}`}>{device.status}</div>
                      </div>
                    </div>
                  </div>
                  <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-all">
                    Wipe Data
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Response Tab */}
        {activeTab === 'response' && (
          <div className="space-y-4">
            <h3 className="text-white font-bold text-lg">Device Management Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {deviceActions.map((action) => {
                const ActionIcon = action.icon
                return (
                  <button
                    key={action.id}
                    className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 p-6 hover:border-slate-500 transition-all shadow-lg text-left group hover:bg-slate-800"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg bg-${action.color}-500/20`}>
                        <ActionIcon className={`w-6 h-6 text-${action.color}-400`} />
                      </div>
                    </div>
                    <h4 className="text-white font-bold mb-1">{action.label}</h4>
                    <p className="text-slate-400 text-sm">{action.desc}</p>
                  </button>
                )
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
